import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Star, Check, Heart } from 'lucide-react';
import { productSlug } from '@/utils/slugify';
import { useCart } from '@/context/CartContext';
import { useFavorites } from '@/context/FavoritesContext';
import { toast } from '@/components/ui/use-toast';

function StarRating({ rating, count }) {
  if (!rating) return null;
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map(s => (
          <Star
            key={s}
            className={`w-3 h-3 ${s <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'}`}
          />
        ))}
      </div>
      {count > 0 && <span className="text-[11px] text-gray-600">({count})</span>}
    </div>
  );
}

const isOutOfStock = (p) => p.stock_quantity !== undefined && p.stock_quantity !== null && p.stock_quantity === 0;

export default function CSProductCard({ product }) {
  const { addItem, items } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [added, setAdded] = useState(false);

  if (!product || !product.id || !product.name || product.price == null) return null;
  // Sanitize name in case of leading/trailing whitespace or tab characters
  const productName = String(product.name).trim();
  const favorite = isFavorite(product.id);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Vérification du stock côté UI (le contrôle serveur reste au paiement)
    const stock = product.stock_quantity;
    if (stock != null) {
      const inCart = items.find(i => i.product_id === product.id)?.quantity || 0;
      if (inCart + 1 > stock) {
        toast({
          variant: 'destructive',
          title: 'Stock insuffisant',
          description: stock > 0
            ? `Il ne reste que ${stock} exemplaire${stock > 1 ? 's' : ''} de ${productName}.`
            : `${productName} n'est plus en stock.`,
        });
        return;
      }
    }
    addItem(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const discount = product.compare_price
    ? Math.round((1 - product.price / product.compare_price) * 100)
    : null;

  return (
    <div className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex flex-col">
      <Link to={`/product/${productSlug(product)}`} className="block relative" aria-label={`Voir le produit ${productName}`}>
        <div className="aspect-square bg-gray-50 overflow-hidden">
          <img
            src={product.thumbnail_url || product.image_url}
            alt=""
            width="400"
            height="400"
            loading="lazy"
            decoding="async"
            sizes="(max-width: 768px) 50vw, 25vw"
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 p-2"
          />
        </div>
        {/* Favorite button */}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(product); }}
          aria-label={favorite ? `Retirer ${productName} des favoris` : `Ajouter ${productName} aux favoris`}
          className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow hover:scale-110 transition-transform z-10"
        >
          <Heart className={`w-4 h-4 transition-colors ${favorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
        </button>
        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {product.badge && (
            <span className="text-[10px] font-bold px-2 py-0.5 bg-cs-blue text-white rounded">
              {product.badge}
            </span>
          )}
          {discount && (
            <span className="text-[10px] font-bold px-2 py-0.5 bg-red-500 text-white rounded">
              -{discount}%
            </span>
          )}
        </div>
      </Link>

      <div className="p-3 flex flex-col flex-1">
        {product.brand && (
          <p className="text-[10px] font-bold text-cs-blue-dark uppercase tracking-wide mb-1">{product.brand}</p>
        )}
        <Link to={`/product/${productSlug(product)}`}>
          <h3 className="text-sm font-medium text-gray-900 leading-tight hover:text-cs-blue transition-colors line-clamp-2 mb-1">
            {productName}
          </h3>
        </Link>
        {product.reference && (
          <p className="text-[10px] text-gray-600 mb-2">Réf: {product.reference.replace(/ /g, '-')}</p>
        )}
        <StarRating rating={product.rating} count={product.review_count} />

        <div className="flex items-end justify-between mt-auto pt-3">
          <div>
            <p className="text-base font-bold text-gray-900">{product.price?.toFixed(2)} €</p>
            {product.compare_price && (
              <p className="text-xs text-gray-400 line-through">{product.compare_price.toFixed(2)} €</p>
            )}
          </div>
          <button
            onClick={handleAdd}
            disabled={isOutOfStock(product)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded text-xs font-bold transition-all duration-300 ${
              added
                ? 'bg-green-500 text-white'
                : !isOutOfStock(product)
                  ? 'bg-cs-blue text-white hover:bg-cs-blue-dark'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {added ? (
              <><Check className="w-3.5 h-3.5" /> OK</>
            ) : isOutOfStock(product) ? (
              'Rupture'
            ) : (
              <><ShoppingCart className="w-3.5 h-3.5" /> Ajouter</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}